import React, { useMemo, useState, useEffect, useCallback, useRef, useLayoutEffect, useImperativeHandle, memo } from 'react';
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useMultipleEntityOptions } from '@/hooks/useEntityOptions';
import { isSeparatorRow, createEmptyRow, resolveColumnReadOnly, toDisplayString, sortRowsByColumn, isValueInDateRange, resolveFilterVariant, resolveExportCell, type ColumnDateRange } from './utils';
import { cn } from '@/lib/utils';
import type { EditableTableProps, ColumnConfig, ColumnGroup, ColumnSortState, EntityCellConfig, RowState } from './types';
import { useEditableTableState } from './useEditableTableState';
import { useEditingState } from './useEditingState';
import { useKeyboardNavigation } from './useKeyboardNavigation';
import { Toolbar, type ToolbarDownload } from './Toolbar';
import { ActionCell } from './ActionCell';
import { buildRowMenuItems } from './buildRowMenuItems';
import { EditableCell } from './EditableCell';
import { StickyScrollbar } from './StickyScrollbar';
import { FloatingTableHeader } from './FloatingTableHeader';
import { ColumnFilter, FILTER_EMPTY_VALUE } from './ColumnFilter';
import { DateRangeColumnFilter } from './DateRangeColumnFilter';
import { ColumnManagerPanel } from './ColumnManagerPanel';
import { ColumnResizeHandle } from './ColumnResizeHandle';
import { TruncatedHeaderLabel } from './TruncatedHeaderLabel';
import { useColumnPrefs } from './useColumnPrefs';
import { useColumnWidths, DEFAULT_COLUMN_WIDTH } from './useColumnWidths';
import { computeStickyMeta, findScrollParent, ACTION_COL_WIDTH, type StickyMeta } from './stickyUtils';
import type { EntityOption } from '@/hooks/useEntityOptions';
import { useTranslation } from '@/hooks/useTranslation';
import { toast } from '@/hooks/useToast';
import { captureException } from '@/lib/sentry';
import type { TabularColumn, TabularFormat } from '@/lib/export/tabularExport';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowDown, ArrowUp, ChevronRight, Plus } from 'lucide-react';
import './editable-table.css';

type RowData = Record<string, unknown>;

/**
 * Get CSS class for a cell based on column type and state
 */
function getCellClassName(column: ColumnConfig, isChanged?: boolean, isReadOnly?: boolean): string {
  const classes: string[] = [];
  if (column.type === 'numeric') classes.push('cell-numeric');
  if (column.type === 'checkbox') classes.push('cell-checkbox');
  if (column.type === 'date') classes.push('cell-date');
  if (column.type === 'time') classes.push('cell-time');
  if (column.className) classes.push(column.className);
  if (isReadOnly) classes.push('cell-readonly');
  if (isChanged) classes.push('cell-changed');
  return classes.join(' ');
}

/**
 * Build grouped header info for columns that belong to a group
 */
function buildGroupedHeaders(
  columns: ColumnConfig[],
  columnGroups?: ColumnGroup[]
): Map<string, { group: ColumnGroup; columns: ColumnConfig[] }> {
  if (!columnGroups?.length) return new Map();

  const grouped = new Map<string, { group: ColumnGroup; columns: ColumnConfig[] }>();
  for (const group of columnGroups) {
    const groupCols = columns.filter(c => c.group === group.key);
    if (groupCols.length > 0) {
      grouped.set(group.key, { group, columns: groupCols });
    }
  }
  return grouped;
}

/**
 * Get row state CSS class
 */
function getRowStateClass(state?: string): string {
  switch (state) {
    case 'modified': return 'row-modified';
    case 'new': return 'row-new';
    case 'deleted': return 'row-deleted';
    default: return '';
  }
}

/**
 * Memoized data row — only re-renders when its own row data, state, or editing changes.
 */
interface DataRowProps {
  rowData: RowData;
  rowIndex: number;
  rowState: RowState | undefined;
  rowChangedCells: Set<string> | undefined;
  rowCellErrors: Map<string, string> | undefined;
  columns: ColumnConfig[];
  readOnly: boolean;
  hideActionColumn: boolean;
  actionColumnActions?: ('rollback' | 'delete' | 'add')[];
  renderActionCell?: (rowData: RowData) => React.ReactNode;
  renderRowMenu?: (
    rowData: RowData,
    helpers: { softDelete: () => void },
  ) => React.ReactNode;
  rowIsReadOnly: boolean;
  rowIdField: string;
  editingColId: string | null; // only the col being edited in THIS row, null otherwise
  entityOptionsMap: Record<string, EntityOption[]> | undefined;
  entityOptionsLoading: boolean;
  locale: string | undefined;
  cellClassName?: (rowData: RowData, colKey: string, value: unknown) => string | undefined;
  onCellClick?: (rowData: RowData, colKey: string) => void;
  onStartEditing: (rowId: string, colId: string) => void;
  onCellChange: (rowId: string, colId: string, value: unknown) => void;
  onStartUndoGroup: () => void;
  onEndUndoGroup: () => void;
  onCommitEdit: () => void;
  onCancelEdit: () => void;
  onRowRollback: (rowId: string) => void;
  onRowDelete: (rowId: string) => void;
  onSetFocusedCell: (rowIndex: number, colIndex: number, shiftKey?: boolean) => void;
  getCellSelectionClasses: (rowIndex: number, colIndex: number) => string;
  stickyMeta: StickyMeta;
  /** Per-column `<TableCell>` style, memoized once in `EditableTable` and
   *  reused across rows so cells aren't handed a fresh inline object each
   *  render. Keyed by `col.data`. */
  cellStyleByCol: Map<string, React.CSSProperties>;
  /** Style for the leading action / expand cell. Stable per `stickyMeta`. */
  actionCellStyle: React.CSSProperties;
  // Expand support
  showExpandColumn: boolean;
  isExpandable: boolean;
  isExpanded: boolean;
  expandRowId: string;
  onRowExpand: (rowId: string) => void;
  hideExpandMenuItem: boolean;
  expandTrigger: 'menu' | 'chevron';
}

const DataRow = memo(function DataRow({
  rowData,
  rowIndex,
  rowState,
  rowChangedCells,
  rowCellErrors,
  columns,
  readOnly,
  hideActionColumn,
  actionColumnActions,
  renderActionCell,
  renderRowMenu,
  rowIsReadOnly,
  rowIdField,
  editingColId,
  entityOptionsMap,
  entityOptionsLoading,
  locale,
  cellClassName,
  onCellClick,
  onStartEditing,
  onCellChange,
  onStartUndoGroup,
  onEndUndoGroup,
  onCommitEdit,
  onCancelEdit,
  onRowRollback,
  onRowDelete,
  onSetFocusedCell,
  getCellSelectionClasses,
  stickyMeta,
  cellStyleByCol,
  actionCellStyle,
  showExpandColumn,
  isExpandable,
  isExpanded,
  expandRowId,
  onRowExpand,
  hideExpandMenuItem,
  expandTrigger,
}: DataRowProps) {
  const { t } = useTranslation('spreadsheet');
  const hasId = rowData[rowIdField] !== undefined;
  const isDeleted = rowState === 'deleted';
  const rowId = rowData._rowId as string;
  const effectiveReadOnly = readOnly || rowIsReadOnly;

  const showActionCol = (!readOnly && !hideActionColumn) || showExpandColumn;

  // Action column is visible — `EditableTable` builds the dropdown body
  // (expand-toggle + caller items + delete) and `ActionCell` decides which
  // single affordance to show for the row state (separator / dirty / menu).
  let actionCellContent: React.ReactNode = null;
  if (!readOnly && !hideActionColumn) {
    const callerItems = renderRowMenu?.(rowData, { softDelete: () => onRowDelete(rowId) });
    const expandable = showExpandColumn && isExpandable;
    const menuContent = buildRowMenuItems({
      hasId,
      isExpandable: expandable,
      isExpanded,
      onToggleExpand: expandable ? () => onRowExpand(expandRowId) : undefined,
      onDelete: () => onRowDelete(rowId),
      callerItems,
      actions: actionColumnActions,
      expandRowLabel: t('actions.expandRow'),
      collapseRowLabel: t('actions.collapseRow'),
      deleteRowLabel: t('actions.deleteRow'),
      hideExpandItem: hideExpandMenuItem,
    });
    actionCellContent = renderActionCell ? renderActionCell(rowData) : (
      <ActionCell
        rowState={rowState}
        hasId={hasId}
        actions={actionColumnActions}
        expandDetailId={expandable ? `${expandRowId}-detail` : undefined}
        menuContent={menuContent}
        expandToggle={
          expandable && expandTrigger === 'chevron'
            ? {
                isExpanded,
                onToggle: () => onRowExpand(expandRowId),
                label: isExpanded ? t('actions.collapseRow') : t('actions.expandRow'),
              }
            : undefined
        }
        rowId={rowId}
        onRowRollback={onRowRollback}
      />
    );
  } else if (showExpandColumn && isExpandable && renderActionCell) {
    // Action column hidden (read-only grid), rows expandable, and the caller
    // draws the leading cell itself — `useIndexGridShell`'s expand chevron,
    // which carries the grid's OWN expand / collapse labels.
    actionCellContent = renderActionCell(rowData);
  } else if (showExpandColumn && isExpandable) {
    // Action column hidden (read-only grid) but rows are still expandable —
    // bare chevron button with the generic labels.
    actionCellContent = (
      <button
        type="button"
        className="rollback-btn expand-toggle-btn"
        aria-expanded={isExpanded}
        aria-label={isExpanded ? t('table.collapseRow') : t('table.expandRow')}
        onClick={(e) => { e.stopPropagation(); onRowExpand(expandRowId); }}
      >
        <ChevronRight
          className="w-3.5 h-3.5"
          style={{
            transform: isExpanded ? 'rotate(90deg)' : undefined,
            transition: 'transform 0.15s',
          }}
        />
      </button>
    );
  }

  return (
    <TableRow data-row-id={expandRowId} className={cn(getRowStateClass(rowState), isExpanded && 'row-expanded')} aria-rowindex={rowIndex + 2}>
      {/* Action / expand column */}
      {showActionCol && (
        <TableCell
          className={cn('rollback-cell', stickyMeta.actionColSticky && 'cell-sticky')}
          style={actionCellStyle}
        >
          {actionCellContent}
        </TableCell>
      )}
      {/* Data cells */}
      {columns.map((col, colIdx) => {
        const isChanged = rowChangedCells?.has(col.data);
        const cellValue = rowData[col.data];
        const cellIsEditing = editingColId === col.data;
        // col.readOnly === false (or a predicate returning false) explicitly
        // overrides row-level read-only
        const resolvedColReadOnly = resolveColumnReadOnly(col, rowData);
        const isCellReadOnly = resolvedColReadOnly === false ? false : (resolvedColReadOnly || effectiveReadOnly);
        const selectionClasses = getCellSelectionClasses(rowIndex, colIdx);
        const dynamicClass = cellClassName?.(rowData, col.data, cellValue);
        const stickyLeft = stickyMeta.offsets.get(col.data);
        const isSticky = stickyLeft !== undefined;
        const isLastSticky = col.data === stickyMeta.lastStickyColData;
        const rawError = rowCellErrors?.get(col.data);
        const errorMessage = rawError === 'required' ? t('validation.required') : rawError;
        const errorMessageId = errorMessage ? `cell-error-${rowId}-${col.data}` : undefined;

        return (
          <TableCell
            key={col.data}
            data-row={rowIndex}
            data-col={colIdx}
            aria-colindex={colIdx + 1}
            aria-invalid={errorMessage ? true : undefined}
            aria-errormessage={errorMessageId}
            className={cn(
              getCellClassName(col, isChanged, isCellReadOnly),
              cellIsEditing && 'p-0',
              selectionClasses,
              dynamicClass,
              isSticky && 'cell-sticky',
              isLastSticky && 'cell-sticky-last',
              errorMessage && 'cell-invalid',
            )}
            style={cellStyleByCol.get(col.data)}
            onMouseDown={(e) => {
              if (e.shiftKey) e.preventDefault();
            }}
            onClick={(e) => {
              onSetFocusedCell(rowIndex, colIdx, e.shiftKey);
              onCellClick?.(rowData, col.data);
            }}
          >
            {errorMessage && (
              <>
                <span id={errorMessageId} className="sr-only">{errorMessage}</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="cell-error-marker"
                      aria-hidden="true"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TooltipTrigger>
                  <TooltipContent>{errorMessage}</TooltipContent>
                </Tooltip>
              </>
            )}
            <EditableCell
              rowId={rowId}
              colId={col.data}
              value={cellValue}
              column={col}
              rowData={rowData}
              isEditing={cellIsEditing && !isDeleted}
              readOnly={isCellReadOnly || isDeleted}
              entityOptions={entityOptionsMap?.[col.data]}
              entityOptionsLoading={col.type === 'entity' && entityOptionsLoading}
              locale={locale}
              onStartEditing={onStartEditing}
              onCellChange={onCellChange}
              onStartUndoGroup={onStartUndoGroup}
              onEndUndoGroup={onEndUndoGroup}
              onCommit={onCommitEdit}
              onCancel={onCancelEdit}
            />
          </TableCell>
        );
      })}
    </TableRow>
  );
});

export function EditableTable<T extends Record<string, unknown>>({
  data,
  columns,
  columnGroups,
  onSave,
  onSaveError,
  onDiscard,
  readOnly = false,
  showSaveButtons = true,
  rowIdField = 'id',
  organizationId,
  controlRef,
  onDirtyInfoChange,
  storageKey,
  enableColumnManagement = false,
  computedFields,
  trackedFields,
  locale,
  isRowReadOnly,
  cellClassName,
  onCellClick,
  toolbarExtra,
  toolbarCenter,
  toolbarRight,
  toolbarTitle,
  hideActionColumn = false,
  allowAddRow = false,
  fillToBottom = false,
  actionColumnActions,
  renderActionCell,
  renderRowMenu,
  expandedRowRender,
  canExpand,
  expandedRows,
  onRowExpand,
  sort,
  onSortChange,
  maxRenderedRows,
  onVisibleRowCountChange,
  onVisibleRowIdsChange,
  renderCapFooter,
  renderEmptyState,
  hideExpandMenuItem = false,
  expandTrigger = 'menu',
  floatingHeader = false,
  displayOnly = false,
  download,
}: Readonly<EditableTableProps<T>>) {
  const { t } = useTranslation('spreadsheet');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // `fillToBottom` appends N trailing blank rows so the grid reaches the
  // bottom of its scroll container. Measurement runs once, the first
  // time the prop is true with the DOM mounted — re-runs would race the
  // fillers we've just inserted (the tbody we measure would already
  // include them). The scroll container is bounded by the caller's flex
  // chain; measuring against its bottom accounts for any sibling panels
  // below the grid (PostProjectActionsPanel etc.), unlike measuring the
  // app footer which would overshoot when such panels are present.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Sticky toolbar (floatingHeader only): the toolbar goes position:fixed when
  // it scrolls above the app content area. `toolbarSlotRef` reserves its height
  // in flow so the grid doesn't jump; `toolbarHeight` offsets the header clone
  // so it pins directly below the fixed toolbar.
  const toolbarSlotRef = useRef<HTMLDivElement>(null);
  const toolbarFixedRef = useRef<HTMLDivElement>(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const [trailingFillerCount, setTrailingFillerCount] = useState(0);
  const hasMeasuredFillersRef = useRef(false);
  useLayoutEffect(() => {
    if (!fillToBottom || hasMeasuredFillersRef.current) return;
    const scroll = scrollContainerRef.current;
    if (!scroll) return;
    const tbody = scroll.querySelector<HTMLTableSectionElement>('tbody');
    if (!tbody) return;
    const firstRow = tbody.querySelector<HTMLTableRowElement>('tr');
    const rowHeight = firstRow
      ? firstRow.getBoundingClientRect().height
      : 40;
    // Anchor the measurement off thead.bottom rather than tbody.top: an
    // empty tbody has zero height and some browsers return an unreliable
    // `top` for it (can collapse above the header), which yielded zero
    // fillers for grids that started with no rows. thead always has the
    // header row, so its bottom is stable regardless of data size.
    const thead = scroll.querySelector<HTMLTableSectionElement>('thead');
    const anchorTop = thead
      ? thead.getBoundingClientRect().bottom
      : tbody.getBoundingClientRect().top;
    const containerBottom = scroll.getBoundingClientRect().bottom;
    const available = containerBottom - anchorTop;
    // +1 overshoots the container bottom by up to one row so the grid
    // always reaches it with no partial gap — a vertical scrollbar is
    // preferred over blank space below the last filler.
    const rowsInView = Math.max(0, Math.floor(available / rowHeight) + 1);

    const MAX_FILLER_ROWS = 30;
    const remaining = rowsInView - data.length;
    setTrailingFillerCount(Math.max(0, Math.min(MAX_FILLER_ROWS, remaining)));
    hasMeasuredFillersRef.current = true;
  }, [fillToBottom, readOnly, data.length]);

  // Append fillers to the data handed to the reducer. Reducer's
  // `_isEmptyRow` handling filters untouched fillers out of `dirtyRows`,
  // so they're a no-op on save until the user types into one.
  const effectiveData = useMemo(() => {
    if (trailingFillerCount === 0) return data;
    const fillers = Array.from({ length: trailingFillerCount }, () =>
      createEmptyRow<T>(columns, rowIdField),
    );
    return [...data, ...fillers];
  }, [data, trailingFillerCount, columns, rowIdField]);

  // State management
  const {
    currentData,
    rowStates,
    changedCells,
    dirtyCount,
    rowErrors,
    errorRowCount,
    isSaving,
    handleCellChange,
    handleRowRollback,
    handleRowDelete,
    handleAddRow,
    handleSave,
    handleDiscard,
    handleUndo,
    startUndoGroup,
    endUndoGroup,
  } = useEditableTableState({
    initialData: effectiveData,
    columns,
    rowIdField,
    computedFields,
    trackedFields,
    onSave,
    onSaveError,
    onDiscard,
    readOnly,
  });

  // `allowAddRow`: guarantee a blank entry row at the top whenever a NEW
  // data reference initializes the table (first load, post-save refetch).
  // Deferred while the grid is dirty — the INIT preserve-dirty guard keeps
  // the user's rows in that case, and a surprise prepend would shift
  // index-based cell focus mid-edit. The dirty bail must NOT consume the
  // data reference: with `dirtyCount` in the deps the effect re-runs when
  // dirtiness clears (save/discard), and the deferred prepend then lands —
  // otherwise a refetch arriving mid-edit would permanently lose the blank
  // row for that reference. No loop risk: an untouched blank row is
  // `_isEmptyRow` and never counts as dirty, and once the ref matches the
  // reference every re-run bails. Ref-guarded so each data reference
  // prepends at most once (StrictMode double-runs included). Runs after
  // the INIT dispatch (the state hook's effect registers first), so the
  // ADD_ROW lands on the freshly-initialized baseline.
  // While `allowAddRow` is off, a NEW data reference re-INITs the table and
  // drops the blank row, so the ref is cleared: turning `allowAddRow` back on
  // must prepend again even for a reference that was prepended before (a
  // caller that switches it off during a search hands the original rows
  // back on clear). The same reference while off keeps the ref — no INIT
  // happened, the blank row is still there, so re-enabling adds no second.
  const lastPrependedDataRef = useRef<unknown>(null);
  useEffect(() => {
    if (!allowAddRow || readOnly) {
      if (lastPrependedDataRef.current !== effectiveData) lastPrependedDataRef.current = null;
      return;
    }
    if (lastPrependedDataRef.current === effectiveData) return;
    if (dirtyCount > 0) return; // defer — retried when dirtyCount drops to 0
    lastPrependedDataRef.current = effectiveData;
    handleAddRow();
  }, [effectiveData, allowAddRow, readOnly, handleAddRow, dirtyCount]);

  // Imperative control surface — lets callers that hide the toolbar (or want
  // multi-row insertion) drive the reducer's flat ADD_ROW from outside.
  useImperativeHandle(
    controlRef,
    () => ({
      addRow: (count = 1) => {
        for (let i = 0; i < count; i++) handleAddRow();
      },
    }),
    [handleAddRow],
  );

  // External dirty/validation observer (see EditableTableProps docs).
  useEffect(() => {
    onDirtyInfoChange?.({ dirtyCount, errorRowCount, isSaving });
  }, [onDirtyInfoChange, dirtyCount, errorRowCount, isSaving]);

  // Editing state
  const {
    editingCell,
    startEditing,
    commitEdit,
    cancelEdit,
  } = useEditingState();

  // Expansion support
  const hasExpandColumn = !!(expandedRowRender && canExpand && onRowExpand);

  // The leading 40px action/expand column renders when row actions are
  // available OR rows are expandable. Every per-column structure (colgroup,
  // header, separator rows) must agree on this, so it's computed once.
  const hasLeadingCol = (!readOnly && !hideActionColumn) || hasExpandColumn;

  // Column management (reorder / show / hide) — opt-in via
  // `enableColumnManagement` + `storageKey`. `managedColumns` (user order,
  // hidden columns removed) is what the table draws and navigates; saving /
  // validation keep using the full `columns` so hidden columns'
  // data survives and re-appears on reveal. Identity-equal to `columns`
  // while the user hasn't deviated from defaults.
  const {
    managedColumns,
    manageableColumns,
    hiddenSet,
    isManaged,
    toggleHidden,
    moveColumn,
    reset: resetColumnPrefs,
  } = useColumnPrefs({
    columns,
    storageKey,
    enabled: enableColumnManagement,
    columnGroups,
  });

  // Single ResizeObserver for the scroll container. Two consumers:
  //   1. Expanded-row content sizing — keeps the --scroll-width CSS var in
  //      sync (imperative write, no re-render).
  //   2. The stretch pass below — `containerWidth` state. Commits are
  //      rAF-throttled (one per frame at most) and DEFERRED while a column
  //      drag is in flight: the drag writes <col> widths imperatively
  //      (see the drag perf contract in useColumnWidths) and a state-driven
  //      colgroup re-render mid-gesture would clobber them. The retry loop
  //      spins only during the rare drag+resize overlap and commits on the
  //      first frame after pointerup. Unchanged widths are a no-op commit
  //      (React bails on equal state).
  const [containerWidth, setContainerWidth] = useState(0);
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const container = el.closest('.editable-table-container');
    let raf = 0;
    // Published unconditionally: expanded-detail panels AND the zero-row empty
    // state both size their sticky content off it, and the latter can be up on
    // a grid with no expand column.
    const syncScrollWidth = () => {
      el.style.setProperty('--scroll-width', `${el.clientWidth}px`);
    };
    const commitWidth = () => {
      raf = 0;
      if (container?.classList.contains('is-resizing')) {
        raf = requestAnimationFrame(commitWidth);
        return;
      }
      setContainerWidth(el.clientWidth);
    };
    const ro = new ResizeObserver(() => {
      syncScrollWidth();
      if (!raf) raf = requestAnimationFrame(commitWidth);
    });
    ro.observe(el);
    syncScrollWidth();
    setContainerWidth(el.clientWidth);
    return () => {
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [hasExpandColumn]);

  // Column filter state — per-column Set of raw cell values the user
  // has chosen to KEEP. Empty map / empty sets = no filter. Keys are
  // `col.data`. Auto-applies on toggle. Declared before `dataRef` so
  // the ref can point at the filtered `visibleData` below.
  const [columnFilters, setColumnFilters] = useState<Map<string, Set<string>>>(
    () => new Map(),
  );

  const handleColumnFilterChange = useCallback(
    (colKey: string, next: Set<string>) => {
      setColumnFilters((prev) => {
        const nextMap = new Map(prev);
        if (next.size === 0) nextMap.delete(colKey);
        else nextMap.set(colKey, next);
        return nextMap;
      });
    },
    [],
  );

  // Date-range column filters (`filterVariant: 'dateRange'`) — the same
  // per-column auto-apply contract as `columnFilters`, holding an
  // inclusive From/To instead of a keep-set. Absent key = no filter.
  const [columnDateRangeFilters, setColumnDateRangeFilters] = useState<
    Map<string, ColumnDateRange>
  >(() => new Map());

  const handleColumnDateRangeChange = useCallback(
    (colKey: string, next: ColumnDateRange | undefined) => {
      setColumnDateRangeFilters((prev) => {
        const nextMap = new Map(prev);
        if (!next || (!next.from && !next.to)) nextMap.delete(colKey);
        else nextMap.set(colKey, next);
        return nextMap;
      });
    },
    [],
  );

  // Unique values per filterable column — fed to the filter popover.
  // Computed from the full (pre-filter) `currentData` so the option
  // list stays stable as the user toggles checkboxes. Date-range-variant
  // columns (incl. `type: 'date'` by default) don't list values, so
  // they're skipped.
  const uniqueValuesByColumn = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const col of managedColumns) {
      if (!col.filterable || resolveFilterVariant(col) === 'dateRange') continue;
      const values = new Set<string>();
      for (const row of currentData) {
        if (isSeparatorRow(row)) continue;
        const str = toDisplayString(row[col.data]);
        values.add(str === '' ? FILTER_EMPTY_VALUE : str);
      }
      map.set(col.data, Array.from(values));
    }
    return map;
  }, [managedColumns, currentData]);

  // Extract entity configs for fetching options. Visible columns only —
  // no point fetching dropdown options for a hidden entity column.
  // (Declared here, above the filter/sort pipeline, because the sort
  // comparator and filter labels resolve entity values through the map.)
  const entityConfigs = useMemo(() => {
    return managedColumns
      .filter((col): col is typeof col & { entityConfig: EntityCellConfig } =>
        col.type === 'entity' && !!col.entityConfig
      )
      .map(col => ({
        key: col.data,
        config: col.entityConfig,
      }));
  }, [managedColumns]);

  const { data: entityOptionsMap, isLoading: entityOptionsLoading } = useMultipleEntityOptions(entityConfigs, organizationId);

  // value → label per entity column — what the sort comparator and the
  // download resolve entity ids through (`resolveCellLabel`). Built once per
  // options change, not per sort/download.
  const entityLabelMaps = useMemo(() => {
    const maps = new Map<string, Map<string, string>>();
    for (const [key, options] of Object.entries(entityOptionsMap ?? {})) {
      maps.set(key, new Map(options.map((o) => [String(o.value), o.label])));
    }
    return maps;
  }, [entityOptionsMap]);
  const entityLabelsFor = useCallback(
    (col: ColumnConfig) => (col.type === 'entity' ? entityLabelMaps.get(col.data) : undefined),
    [entityLabelMaps],
  );

  // Column sort state — render-only, like filters. `null` = data order.
  // Dual-mode: when the `sort` prop is provided (controlled — even as
  // `null`), it is the source of truth, header clicks only report the next
  // state via `onSortChange`, and the internal sort stage is skipped (the
  // caller pre-sorts its dataset). Otherwise `columnSort` runs the show,
  // exactly as before.
  const [columnSort, setColumnSort] = useState<ColumnSortState | null>(null);
  const isSortControlled = sort !== undefined;
  const effectiveSort = isSortControlled ? sort : columnSort;

  const handleSortToggle = useCallback((colKey: string) => {
    const computeNext = (prev: ColumnSortState | null): ColumnSortState | null => {
      if (prev?.col !== colKey) return { col: colKey, direction: 'asc' };
      if (prev.direction === 'asc') return { col: colKey, direction: 'desc' };
      return null;
    };
    if (sort !== undefined) {
      onSortChange?.(computeNext(sort));
    } else {
      setColumnSort(computeNext);
    }
  }, [sort, onSortChange]);

  // Apply column filters. Separator rows pass through so grouped
  // layouts (e.g. monthly separators) don't collapse. Dirty rows are
  // filtered visually only — their state stays in `rowStates` and
  // commits on save even when hidden.
  const filteredData = useMemo(() => {
    if (columnFilters.size === 0 && columnDateRangeFilters.size === 0) return currentData;
    return currentData.filter((row) => {
      if (isSeparatorRow(row)) return true;
      // Untouched blank rows (the allowAddRow entry row, fill-to-bottom
      // fillers) always pass — a filtered view must keep its entry row.
      if (row._isEmptyRow === true) return true;
      for (const [colKey, selected] of columnFilters) {
        const str = toDisplayString(row[colKey]);
        const key = str === '' ? FILTER_EMPTY_VALUE : str;
        if (!selected.has(key)) return false;
      }
      for (const [colKey, range] of columnDateRangeFilters) {
        if (!isValueInDateRange(row[colKey], range)) return false;
      }
      return true;
    });
  }, [currentData, columnFilters, columnDateRangeFilters]);

  // Apply the column sort — a second render-only stage after filtering.
  // The reducer's data order never changes, so mutations (all `_rowId`-
  // based) and dirty tracking are unaffected; `dataRef` below tracks the
  // sorted view, keeping keyboard-nav/paste indices aligned with what's
  // on screen. Rules:
  //   - Skipped entirely when separators are present (grouped layouts
  //     own their ordering).
  //   - Rows without a db id (in-progress new rows) stay pinned to the
  //     top in their current order — sorting must never scatter
  //     bulk-entry rows through the persisted set.
  //   - Empty values sort last in both directions.
  //   - `numeric`/`checkbox` compare numerically; everything else by
  //     display string (`displayValue` → entity label → raw) — see
  //     `sortRowsByColumn` in ./utils, the single comparator.
  //   - CONTROLLED sort (`sort` prop provided) skips this stage entirely:
  //     the caller pre-sorts its full dataset before slicing/passing
  //     `data`, so re-sorting the (possibly capped) slice here would be
  //     wrong. The header still reflects `effectiveSort` below.
  const visibleData = useMemo(() => {
    if (isSortControlled) return filteredData;
    if (!columnSort) return filteredData;
    const col = columns.find((c) => c.data === columnSort.col);
    if (!col) return filteredData;
    if (filteredData.some(isSeparatorRow)) return filteredData;

    return sortRowsByColumn(filteredData, col, columnSort.direction, {
      rowIdField,
      entityLabels: entityLabelsFor(col),
    });
  }, [filteredData, isSortControlled, columnSort, columns, entityLabelsFor, rowIdField]);

  // Post-filter, PRE-cap count of real rows — what `onVisibleRowCountChange`
  // reports. Separator rows and untouched blank rows (`_isEmptyRow`) are
  // excluded: they're layout/entry chrome, not data the user filtered to.
  const visibleRowCount = useMemo(
    () =>
      visibleData.reduce(
        (n, row) => (isSeparatorRow(row) || row._isEmptyRow === true ? n : n + 1),
        0,
      ),
    [visibleData],
  );
  useEffect(() => {
    onVisibleRowCountChange?.(visibleRowCount);
  }, [onVisibleRowCountChange, visibleRowCount]);

  // Post-filter, PRE-cap ids of real rows — the id-list companion to
  // `visibleRowCount` above, with the same exclusions (separator rows and
  // untouched blank rows are layout/entry chrome, not data the user
  // filtered to). Reported via `onVisibleRowIdsChange`; computed only when
  // a listener is wired so the many grids without one pay nothing.
  const visibleRowIds = useMemo(
    () =>
      onVisibleRowIdsChange
        ? visibleData
            .filter((row) => !isSeparatorRow(row) && row._isEmptyRow !== true)
            .map((row) => String(row[rowIdField]))
        : null,
    [onVisibleRowIdsChange, visibleData, rowIdField],
  );
  useEffect(() => {
    if (visibleRowIds) onVisibleRowIdsChange?.(visibleRowIds);
  }, [onVisibleRowIdsChange, visibleRowIds]);

  // Render cap — the LAST stage of the render pipeline (filter → sort →
  // cap), so filter options (`uniqueValuesByColumn`, from full
  // `currentData`) and the filter/sort stages above always see the whole
  // dataset; only what reaches the DOM is capped. Rules:
  //   - Untouched blank rows (`_isEmptyRow: true` — the allowAddRow entry
  //     row, fillToBottom fillers) always survive the cap: they're
  //     re-appended after the slice so a capped view keeps its entry row.
  //   - Skipped entirely when separator rows are present (grouped layouts
  //     own their layout — same rule as sorting).
  // Returns BOTH the rows to render AND whether the cap actually dropped any.
  // `capped` is set in the exact branch that slices, so it's the single source
  // of truth for the `renderCapFooter` gate — the footer can never disagree
  // with what was rendered (no parallel re-derivation to keep in sync).
  // Everything downstream that renders rows or tracks row indices (`dataRef`,
  // keyboard nav, TanStack's row model) uses `renderData` so on-screen indices
  // stay aligned with what's rendered.
  const renderResult = useMemo(() => {
    if (maxRenderedRows == null) return { rows: visibleData, capped: false };
    if (visibleData.some(isSeparatorRow)) return { rows: visibleData, capped: false };
    const blanks = visibleData.filter((row) => row._isEmptyRow === true);
    const content =
      blanks.length === 0
        ? visibleData
        : visibleData.filter((row) => row._isEmptyRow !== true);
    if (content.length <= maxRenderedRows) return { rows: visibleData, capped: false };
    return { rows: [...content.slice(0, maxRenderedRows), ...blanks], capped: true };
  }, [visibleData, maxRenderedRows]);
  const renderData = renderResult.rows;

  // The cap-footer (`renderCapFooter`, a bottom "Show all" affordance) shows
  // exactly when the cap dropped rows — read straight off `renderResult`, never
  // re-derived, so it can't drift from what `renderData` actually rendered.
  const isRenderCapped = renderResult.capped;

  // Content rows only — separators carry no data-column content and untouched
  // `fillToBottom` fillers are blank, so neither should "confirm" a
  // measurement that width-less columns rely on for content sizing, nor count
  // as data for the zero-row empty state below.
  const hasContentRows = useMemo(
    () =>
      renderData.some(
        (row) => !isSeparatorRow(row) && row._isEmptyRow !== true,
      ),
    [renderData],
  );

  // ---- Zero-row empty state --------------------------------------------------
  // Opt-in via `renderEmptyState` (index grids get it from `useIndexGridShell`).
  // It REPLACES the body rows, so untouched `fillToBottom` fillers can never
  // render alongside it — the "header over blank ruled rows" state this fixes.
  // Suppressed on data-entry grids (`allowAddRow`): there the blank entry row
  // is the affordance and an empty state would talk over it.
  const hasColumnFilters = columnFilters.size > 0 || columnDateRangeFilters.size > 0;
  const showEmptyState = !!renderEmptyState && !hasContentRows && !allowAddRow;

  // Stable refs — callbacks read from refs to avoid identity changes.
  // `dataRef` tracks the **rendered** (post-filter, post-cap) data so
  // TanStack row indices used by keyboard nav / paste-fill line up with
  // the rendered rows. Pointing it at `currentData` would desync indices
  // as soon as any column filter became active.
  const dataRef = useRef(renderData);
  useLayoutEffect(() => { dataRef.current = renderData; }, [renderData]);

  // Stable cell callbacks — identity never changes, avoids EditableCell re-renders
  const startEditingRef = useRef(startEditing);
  startEditingRef.current = startEditing;
  const handleCellChangeRef = useRef(handleCellChange);
  handleCellChangeRef.current = handleCellChange;

  const stableStartEditing = useCallback((rowId: string, colId: string) => {
    startEditingRef.current(rowId, colId);
  }, []);

  const stableCellChange = useCallback((rowId: string, colId: string, value: unknown) => {
    handleCellChangeRef.current(rowId, colId, value);
  }, []);

  // Stable expand callback — identity never changes, avoids DataRow re-renders
  const onRowExpandRef = useRef(onRowExpand);
  onRowExpandRef.current = onRowExpand;
  const stableOnRowExpand = useCallback((rowId: string) => {
    onRowExpandRef.current?.(rowId);
  }, []);

  // Stable callbacks — identity never changes (read from ref)
  const stableIsRowSeparator = useCallback((rowIndex: number) => {
    return isSeparatorRow(dataRef.current[rowIndex] ?? {});
  }, []);

  // O(1) colId → column lookup. The read-only resolution below runs per cell in
  // paste/delete loops, so avoid a linear columns.find() on every call.
  const colByData = useMemo(() => {
    const m = new Map<string, ColumnConfig>();
    for (const c of columns) m.set(c.data, c);
    return m;
  }, [columns]);

  const stableIsColumnReadOnly = useCallback((colId: string, rowIndex: number) => {
    const col = colByData.get(colId);
    if (!col) return false;
    return resolveColumnReadOnly(col, dataRef.current[rowIndex]) === true;
  }, [colByData]);

  // Returns true if a column explicitly overrides row-level read-only for this
  // row (boolean `false`, or a predicate returning `false`)
  const columnOverridesRowReadOnly = useCallback((colId: string, rowIndex: number) => {
    const col = colByData.get(colId);
    if (!col) return false;
    return resolveColumnReadOnly(col, dataRef.current[rowIndex]) === false;
  }, [colByData]);

  const stableGetCellValue = useCallback((rowIndex: number, colId: string) => {
    return dataRef.current[rowIndex]?.[colId];
  }, []);

  const stableSetCellValue = useCallback((rowIndex: number, colId: string, value: unknown) => {
    const rowId = dataRef.current[rowIndex]?._rowId as string;
    if (rowId) handleCellChangeRef.current(rowId, colId, value);
  }, []);

  const stableGetRowId = useCallback((rowIndex: number) => {
    return dataRef.current[rowIndex]?._rowId as string | undefined;
  }, []);

  // Stable ref for isRowReadOnly
  const isRowReadOnlyRef = useRef(isRowReadOnly);
  isRowReadOnlyRef.current = isRowReadOnly;

  const stableIsRowReadOnly = useCallback((rowIndex: number) => {
    const row = dataRef.current[rowIndex];
    if (!row || !isRowReadOnlyRef.current) return false;
    return isRowReadOnlyRef.current(row);
  }, []);

  // Keyboard navigation — fed the *visible* column set so column indices
  // line up with rendered cells (mirrors dataRef tracking renderData).
  const { tableRef, handleKeyDown, setFocusedCell, clearCellFocus, getCellSelectionClasses } = useKeyboardNavigation({
    columns: managedColumns,
    // While the empty state is up, no cells exist — report zero so arrow keys
    // can't focus a row that isn't in the DOM (fillers survive in `renderData`).
    rowCount: showEmptyState ? 0 : renderData.length,
    editingCell,
    readOnly,
    locale,
    isRowSeparator: stableIsRowSeparator,
    isRowReadOnly: stableIsRowReadOnly,
    isColumnReadOnly: stableIsColumnReadOnly,
    columnOverridesRowReadOnly,
    getCellValue: stableGetCellValue,
    setCellValue: stableSetCellValue,
    getRowId: stableGetRowId,
    onUndo: handleUndo,
    startUndoGroup,
    endUndoGroup,
    startEditing,
    commitEdit,
    cancelEdit,
  });

  // The visible column set as a content signature. Keyed on *content*, not
  // array identity — callers rebuild their columns array on unrelated
  // re-renders (e.g. EditableTableView's editableColumns memo depends on
  // selectedItems, so every row-checkbox click churns the identity), and
  // the focus guard below must not fire for those.
  const managedColumnsSignature = useMemo(
    () => managedColumns.map((c) => c.data).join('|'),
    [managedColumns],
  );

  // Clear focus whenever active filters or the visible column set change —
  // the previously-focused rowIndex/colIndex may now point at a different
  // (or no longer rendered) row/column, so arrow keys / paste would operate
  // on the wrong cell. Cheaper than clamping; users click to refocus anyway.
  useEffect(() => {
    setFocusedCell(-1, -1);
  }, [columnFilters, columnDateRangeFilters, managedColumnsSignature, setFocusedCell]);

  // Drop the filter of a column the user just hid — an invisible filter
  // still narrowing the rows would be unexplainable. Re-shown columns
  // start unfiltered.
  useEffect(() => {
    if (hiddenSet.size === 0) return;
    const dropHidden = <V,>(prev: Map<string, V>): Map<string, V> => {
      let changed = false;
      const next = new Map(prev);
      for (const key of prev.keys()) {
        if (hiddenSet.has(key)) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    };
    setColumnFilters(dropHidden);
    setColumnDateRangeFilters(dropHidden);
  }, [hiddenSet]);

  // TanStack Table — model layer only (sorting, filtering, pagination ready when needed)
  // Rendering uses our memoized DataRow, NOT TanStack's flexRender/cell.getContext()
  const tanstackColumns = useMemo<ColumnDef<RowData>[]>(
    () => managedColumns.map(col => ({ accessorKey: col.data, header: col.header })),
    [managedColumns]
  );

  const table = useReactTable({
    data: renderData as RowData[],
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Column widths — user drag-resize overrides (persisted per `storageKey`)
  // merged with configured widths and a measure-then-fix pass for width-less
  // columns. Once every column has a resolved width (`layoutReady`), the
  // table switches to `table-layout: fixed` driven by the <colgroup> below.
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const { effectiveColumns, layoutReady, startResize, resetWidth } = useColumnWidths({
    columns: managedColumns,
    storageKey,
    headerRowRef,
    hasRows: hasContentRows,
  });
  const resizeHandleLabel = t('resize.handleLabel');

  // Stretch pass — under `table-layout: fixed` + `min-width: 100%`, a table
  // whose columns sum to less than the container is stretched by the browser
  // proportionally across EVERY column — including the leading 40px
  // action/expand column, which must stay fixed (verified in Chromium: with
  // only px-width cols, extra space is distributed pro-rata to all of them).
  // Pre-distribute the extra space here instead: scale the stretchable data
  // columns so the colgroup sums to exactly the container width. The browser
  // then has nothing left to distribute; the action column keeps its 40px
  // and `noStretch` columns (fixed-purpose cells like the entity-grid
  // selection checkbox) keep their exact configured width. Render-time only
  // — configured/stored widths (drag baselines, persistence, reset targets)
  // stay unscaled.
  const renderColumns = useMemo(() => {
    if (!layoutReady || containerWidth <= 0) return effectiveColumns;
    const widthOf = (c: ColumnConfig) => c.width ?? DEFAULT_COLUMN_WIDTH;
    const sum = effectiveColumns.reduce((acc, c) => acc + widthOf(c), 0);
    const available = containerWidth - (hasLeadingCol ? ACTION_COL_WIDTH : 0);
    if (sum <= 0 || available <= sum) return effectiveColumns;
    // If a config marks every column noStretch, scale them all anyway —
    // leaving the colgroup short would hand the leftover back to the
    // browser, which stretches every column, action column included.
    let stretchable = effectiveColumns.filter(c => !c.noStretch);
    if (stretchable.length === 0) stretchable = effectiveColumns;
    const stretchableSum = stretchable.reduce((acc, c) => acc + widthOf(c), 0);
    if (stretchableSum <= 0) return effectiveColumns;
    const stretchableSet = new Set(stretchable);
    const factor = (available - (sum - stretchableSum)) / stretchableSum;
    return effectiveColumns.map(c =>
      stretchableSet.has(c)
        ? { ...c, width: Math.round(widthOf(c) * factor * 100) / 100 }
        : c,
    );
  }, [layoutReady, containerWidth, effectiveColumns, hasLeadingCol]);

  // Rendered width per column (post-stretch) — header cells and grouped
  // sub-headers read this so they line up with the colgroup tracks.
  const renderWidthByCol = useMemo(() => {
    const map = new Map<string, number | undefined>();
    for (const col of renderColumns) map.set(col.data, col.width);
    return map;
  }, [renderColumns]);

  // Sticky column metadata — computed from rendered (post-stretch) widths so
  // offsets track both user-resized and container-stretched columns.
  const stickyMeta = useMemo(
    () => computeStickyMeta(renderColumns, { hideActionColumn, readOnly, columnGroups, hasExpandColumn }),
    [renderColumns, hideActionColumn, readOnly, columnGroups, hasExpandColumn]
  );

  // Per-column `<TableCell>` style, computed once per (columns × stickyMeta)
  // change and reused across every row. Without this, each cell got a fresh
  // inline `{ width, left? }` object every render, which alone triggered
  // 450+ TableCell re-renders for a 50-row × 8-col grid.
  const cellStyleByCol = useMemo(() => {
    const map = new Map<string, React.CSSProperties>();
    for (const col of renderColumns) {
      const stickyLeft = stickyMeta.offsets.get(col.data);
      map.set(col.data, {
        // Under fixed layout (`layoutReady`) the <colgroup> drives column
        // widths — td widths are ignored by the browser, so omit them.
        ...(!layoutReady && col.width ? { width: `${col.width}px` } : undefined),
        ...(stickyLeft !== undefined ? { left: `${stickyLeft}px` } : undefined),
      });
    }
    return map;
  }, [renderColumns, layoutReady, stickyMeta]);

  // Style for the leading action / expand cell — stable per stickyMeta change.
  const actionCellStyle = useMemo<React.CSSProperties>(
    () => ({
      width: ACTION_COL_WIDTH,
      minWidth: ACTION_COL_WIDTH,
      maxWidth: ACTION_COL_WIDTH,
      padding: 0,
      textAlign: 'center',
      ...(stickyMeta.actionColSticky ? { left: 0 } : undefined),
    }),
    [stickyMeta.actionColSticky],
  );

  // Style for separator-row leading cells and expanded-detail leading cells.
  // Same shape as `actionCellStyle` minus the centering — used by short cells
  // that only need the column width + sticky offset.
  const leadingCellStyle = useMemo<React.CSSProperties>(
    () => ({
      width: ACTION_COL_WIDTH,
      padding: 0,
      ...(stickyMeta.actionColSticky ? { left: 0 } : undefined),
    }),
    [stickyMeta.actionColSticky],
  );

  // Sticky offset for separator-row label cells. Sits right after the action
  // column when present, flush left otherwise.
  const separatorLabelStyle = useMemo<React.CSSProperties>(
    () => ({ left: stickyMeta.actionColSticky ? ACTION_COL_WIDTH : 0 }),
    [stickyMeta.actionColSticky],
  );

  // Build grouped header info
  const groupedHeaders = useMemo(
    () => buildGroupedHeaders(columns, columnGroups),
    [columns, columnGroups]
  );

  // Determine which columns are part of a group
  const groupedColumnKeys = useMemo(() => {
    const keys = new Set<string>();
    groupedHeaders.forEach(({ columns: groupCols }) => {
      groupCols.forEach(c => keys.add(c.data));
    });
    return keys;
  }, [groupedHeaders]);

  // Handle fullscreen escape — skip if editing or selection is active
  useEffect(() => {
    if (!isFullscreen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !editingCell) setIsFullscreen(false);
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isFullscreen, editingCell]);

  const toggleFullscreen = useCallback(() => setIsFullscreen(prev => !prev), []);

  // `aria-rowcount` must describe what's actually in the DOM — the empty state
  // renders no data rows (any surviving fillers are skipped, see the body).
  const totalRows = showEmptyState ? 0 : renderData.length;
  const totalCols = managedColumns.length;

  const hasToolbar =
    showSaveButtons || !!download || !!toolbarCenter || !!toolbarRight || (isFullscreen && !!toolbarTitle);

  // Dev guard: the Columns trigger lives in the toolbar, or — toolbar-less —
  // in the leading header cell. Flag configs where management is on but
  // neither exists — otherwise the prop silently does nothing (mirrors
  // stickyUtils' console.warn convention for config conflicts).
  useEffect(() => {
    if (isManaged && !hasLeadingCol && !hasToolbar) {
      console.warn(
        '[EditableTable] enableColumnManagement is set but the table has no toolbar and no leading column '
        + '(action column hidden / readOnly, and no expandable rows) — the Columns trigger has nowhere to render.',
      );
    }
  }, [isManaged, hasLeadingCol, hasToolbar]);

  // ---- Table download (`download` prop) --------------------------------------
  // Exports what the table shows: `visibleData` (caller order + this table's
  // column filters + its own sort), BEFORE the render cap, minus separators,
  // untouched blank rows and rows staged for delete; the visible columns in
  // the user's order, minus internal `__` and `exportable: false` columns.
  // Staged (unsaved) edits are exported as shown. tabularExport (papaparse +
  // write-excel-file) is imported on click, so it never weighs on the table.
  // The rows a download writes — one filter for the file AND the disabled state
  // (so a table whose shown rows are all staged for delete can't produce an
  // empty file).
  const exportableRows = useMemo(
    () =>
      download
        ? visibleData.filter(
            (row) =>
              !isSeparatorRow(row)
              && row._isEmptyRow !== true
              && rowStates.get(row._rowId as string) !== 'deleted',
          )
        : [],
    [download, visibleData, rowStates],
  );
  const handleDownload = async (format: TabularFormat) => {
    if (!download) return;
    try {
      const exportColumns = managedColumns.filter(
        (col) => col.exportable !== false && !col.data.startsWith('__'),
      );
      const widthByCol = new Map(effectiveColumns.map((col) => [col.data, col.width]));
      const groupLabelByKey = new Map((columnGroups ?? []).map((g) => [g.key, g.label]));
      const tableColumns: TabularColumn[] = exportColumns.map((col) => {
        const groupLabel = col.group ? groupLabelByKey.get(col.group) : undefined;
        return {
          header: groupLabel ? `${groupLabel} – ${col.header}` : col.header,
          widthPx: widthByCol.get(col.data) ?? DEFAULT_COLUMN_WIDTH,
          date: col.type === 'date',
        };
      });
      const rows = exportableRows.map((row) =>
        exportColumns.map((col) => resolveExportCell(col, row, entityLabelsFor(col))),
      );
      const { downloadTable } = await import('@/lib/export/tabularExport');
      await downloadTable({
        fileName: typeof download.fileName === 'function' ? download.fileName() : download.fileName,
        sheetName: download.sheetName,
        format,
        columns: tableColumns,
        rows,
      });
    } catch (error) {
      captureException(error, { feature: 'editable-table', action: 'download', format });
      toast.error(t('toolbar.downloadFailed'));
    }
  };
  const toolbarDownload: ToolbarDownload | undefined = download
    ? {
        disabled: exportableRows.length === 0 || entityOptionsLoading,
        onDownload: (format) => void handleDownload(format),
      }
    : undefined;
  const columnsPanelNode = isManaged ? (
    <ColumnManagerPanel
      columns={manageableColumns}
      hiddenSet={hiddenSet}
      onToggleHidden={toggleHidden}
      onMove={moveColumn}
      onReset={resetColumnPrefs}
      toolbar={hasToolbar}
    />
  ) : null;

  // Toolbar element — extracted so the floatingHeader path can wrap it in a
  // sticky slot (below) without duplicating the prop list.
  const toolbarNode = hasToolbar ? (
    <Toolbar
      dirtyCount={dirtyCount}
      errorRowCount={errorRowCount}
      isSaving={isSaving}
      isFullscreen={isFullscreen}
      onSave={showSaveButtons ? handleSave : undefined}
      onDiscard={showSaveButtons ? handleDiscard : undefined}
      onToggleFullscreen={toggleFullscreen}
      extra={toolbarExtra}
      center={toolbarCenter}
      right={toolbarRight}
      title={toolbarTitle}
      columnsPanel={columnsPanelNode}
      download={toolbarDownload}
    />
  ) : null;

  // Sticky toolbar controller (floatingHeader only) — pin the real toolbar to
  // the top of the app content area when it scrolls off, with a spacer so the
  // grid doesn't jump. `toolbarHeight` feeds the header clone's `stickyOffset`
  // so the pinned header stacks directly beneath it. Positioning is imperative;
  // the follow loop mirrors FloatingTableHeader/StickyScrollbar.
  useEffect(() => {
    const enabled = floatingHeader && hasToolbar && !isFullscreen;
    const slot = toolbarSlotRef.current;
    const tb = toolbarFixedRef.current;
    const scroll = scrollContainerRef.current;
    const resetToolbar = () => {
      if (tb) {
        tb.style.position = '';
        tb.style.top = '';
        tb.style.left = '';
        tb.style.width = '';
        tb.classList.remove('is-pinned');
      }
      if (slot) slot.style.height = '';
    };
    if (!enabled || !slot || !tb || !scroll) {
      resetToolbar();
      setToolbarHeight(0);
      return;
    }
    // Resolve the scroll viewport lazily (mirrors FloatingTableHeader):
    // `findScrollParent` skips declared-but-non-clipping containers (an
    // auto-height, overflow-y:auto TabsContent), so it can return null until the
    // grid grows tall enough to make a real scroller overflow. Re-resolve until
    // we latch one.
    let scrollParent = findScrollParent(scroll);
    let raf = 0;
    let lastHeight = 0;
    const measure = () => {
      raf = 0;
      if (!scrollParent) scrollParent = findScrollParent(scroll);
      const snapTop = scrollParent ? scrollParent.getBoundingClientRect().top : 0;
      const slotRect = slot.getBoundingClientRect();
      const height = tb.offsetHeight;
      if (height !== lastHeight) {
        lastHeight = height;
        setToolbarHeight(height);
      }
      if (slotRect.top < snapTop - 0.5) {
        tb.style.position = 'fixed';
        tb.style.top = `${snapTop}px`;
        tb.style.left = `${slotRect.left}px`;
        tb.style.width = `${slotRect.width}px`;
        tb.classList.add('is-pinned');
        slot.style.height = `${height}px`;
      } else {
        tb.style.position = '';
        tb.style.top = '';
        tb.style.left = '';
        tb.style.width = '';
        tb.classList.remove('is-pinned');
        slot.style.height = '';
      }
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    const ro = new ResizeObserver(schedule);
    ro.observe(scroll);
    ro.observe(tb);
    scroll.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('scroll', schedule, { passive: true, capture: true });
    return () => {
      ro.disconnect();
      scroll.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('scroll', schedule, true);
      if (raf) cancelAnimationFrame(raf);
      resetToolbar();
    };
  }, [floatingHeader, hasToolbar, isFullscreen]);

  // Shared header row — rendered here for the real header and reused verbatim
  // by the floating clone (FloatingTableHeader) so the two stay structurally
  // identical and fully functional: the clone gets the SAME sort/filter/resize
  // handlers, which drive the real table via `headerRowRef`. `isClone` only
  // drops the ref so the clone never becomes the measurement/resize anchor.
  const renderHeaderRow = (isClone: boolean) => (
    <TableRow ref={isClone ? undefined : headerRowRef} aria-rowindex={1}>
      {/* Action / expand column header */}
      {hasLeadingCol && (
        <TableHead
          className={cn(stickyMeta.actionColSticky && 'cell-sticky')}
          style={{
            width: ACTION_COL_WIDTH, minWidth: ACTION_COL_WIDTH, maxWidth: ACTION_COL_WIDTH, padding: 0, textAlign: 'center',
            ...(stickyMeta.actionColSticky ? { left: 0 } : undefined),
          }}
        >
          <div className="flex flex-col items-center justify-center gap-0.5">
            {allowAddRow && !readOnly && !hideActionColumn && (
              // rollback-btn (not separator-add-btn) so the header
              // "+" matches the 28×28 row chevrons below it.
              <button
                type="button"
                className="rollback-btn"
                onClick={() => handleAddRow()}
                title={t('actions.addRow')}
                aria-label={t('actions.addRow')}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
            {/* Toolbar-less fallback — with a toolbar, the trigger
                renders there instead (next to fullscreen). */}
            {!hasToolbar && columnsPanelNode}
          </div>
        </TableHead>
      )}
      {managedColumns.map((col, colIdx) => {
        const stickyLeft = stickyMeta.offsets.get(col.data);
        const isSticky = stickyLeft !== undefined;
        const isLastSticky = col.data === stickyMeta.lastStickyColData;

        // If this column is part of a group, render the grouped header
        if (groupedColumnKeys.has(col.data)) {
          const groupInfo = [...groupedHeaders.values()].find(g =>
            g.columns.some(c => c.data === col.data)
          );
          // Only render on the first column of the group
          if (groupInfo?.columns[0].data === col.data) {
            // Group header is sticky if its first column is sticky
            const groupStickyLeft = stickyMeta.offsets.get(groupInfo.columns[0].data);
            const groupIsSticky = groupStickyLeft !== undefined;
            const lastGroupCol = groupInfo.columns[groupInfo.columns.length - 1];
            const groupIsLastSticky = lastGroupCol.data === stickyMeta.lastStickyColData;

            return (
              <TableHead
                key={col.data}
                colSpan={groupInfo.columns.length}
                aria-colindex={colIdx + 1}
                className={cn(
                  groupIsSticky && 'cell-sticky',
                  groupIsLastSticky && 'cell-sticky-last',
                )}
                style={{
                  padding: 0, verticalAlign: 'top', position: 'relative',
                  ...(groupIsSticky ? { left: `${groupStickyLeft}px` } : undefined),
                }}
              >
                {/* In fixed mode drop the horizontal padding so the
                    explicit sub widths line up exactly with the
                    colgroup tracks below. */}
                <div className="grouped-header" style={{ padding: layoutReady ? '4px 0' : '4px 8px' }}>
                  <div className="grouped-header-label">{groupInfo.group.label}</div>
                  <div className="grouped-header-subs">
                    {groupInfo.columns.map(gc => {
                      const subWidth = renderWidthByCol.get(gc.data);
                      return (
                        <div
                          key={gc.data}
                          data-col-key={gc.data}
                          className="grouped-header-sub"
                          style={subWidth !== undefined ? { width: `${subWidth}px` } : undefined}
                        >
                          {gc.header}
                          {gc.required && <span className="required-asterisk"> *</span>}
                          <ColumnResizeHandle
                            colData={gc.data}
                            label={resizeHandleLabel}
                            onResizeStart={startResize}
                            onReset={resetWidth}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </TableHead>
            );
          }
          // Skip subsequent columns in the group (covered by colspan)
          return null;
        }

        const effectiveWidth = renderWidthByCol.get(col.data);
        return (
          <TableHead
            key={col.data}
            data-col-key={col.data}
            aria-colindex={colIdx + 1}
            aria-sort={
              effectiveSort?.col === col.data
                ? (effectiveSort.direction === 'asc' ? 'ascending' : 'descending')
                : undefined
            }
            className={cn(
              col.className,
              isSticky && 'cell-sticky',
              isLastSticky && 'cell-sticky-last',
            )}
            style={{
              width: effectiveWidth !== undefined ? `${effectiveWidth}px` : undefined,
              ...(isSticky ? { left: `${stickyLeft}px` } : undefined),
            }}
          >
            <div className="group flex items-center gap-1">
              {/* A custom header node replaces the label outright — no sort
                  button wraps it, so a live control (a select-all checkbox)
                  never doubles as a sort toggle. */}
              {col.headerRenderer ? (
                col.headerRenderer()
              ) : col.sortable ? (
                <button
                  type="button"
                  className="flex items-center gap-1 min-w-0 select-none hover:text-foreground"
                  onClick={() => handleSortToggle(col.data)}
                  aria-label={t('sort.toggle', { column: col.header })}
                >
                  <TruncatedHeaderLabel text={col.header} />
                  {effectiveSort?.col === col.data &&
                    (effectiveSort.direction === 'asc' ? (
                      <ArrowUp className="h-3.5 w-3.5 shrink-0 text-primary" />
                    ) : (
                      <ArrowDown className="h-3.5 w-3.5 shrink-0 text-primary" />
                    ))}
                </button>
              ) : (
                <TruncatedHeaderLabel text={col.header} />
              )}
              {col.required && <span className="required-asterisk">*</span>}
              {col.filterable && resolveFilterVariant(col) === 'dateRange' ? (
                <DateRangeColumnFilter
                  columnHeader={col.header}
                  value={columnDateRangeFilters.get(col.data)}
                  onChange={(next) => handleColumnDateRangeChange(col.data, next)}
                />
              ) : col.filterable ? (
                <ColumnFilter
                  columnHeader={col.header}
                  uniqueValues={uniqueValuesByColumn.get(col.data) ?? []}
                  entityOptions={entityOptionsMap?.[col.data]}
                  resolveLabel={col.displayValue}
                  selected={columnFilters.get(col.data) ?? new Set()}
                  onChange={(next) => handleColumnFilterChange(col.data, next)}
                />
              ) : null}
            </div>
            <ColumnResizeHandle
              colData={col.data}
              label={resizeHandleLabel}
              onResizeStart={startResize}
              onReset={resetWidth}
            />
          </TableHead>
        );
      })}
    </TableRow>
  );

  return (
    <div
      className={cn(
        'editable-table-container',
        // Neutral read-only cells (see `displayOnly` in types.ts and the
        // "Display-only grids" block in editable-table.css).
        displayOnly && 'editable-table--display-only',
        // Reserve 14px at the bottom for the StickyScrollbar. The bar is
        // position: fixed, so without this reserve it would overlap
        // whatever sits below the table. Fullscreen mode uses a sticky
        // bar inside the scroll container, so no reserve needed there.
        !isFullscreen && 'pb-3.5',
        isFullscreen && 'fixed inset-0 z-50 bg-background fullscreen'
      )}
    >
      {/* Toolbar. When a toolbar renders, the Columns-panel trigger lives
          in it (next to fullscreen) — the standard placement for editable
          tables. Toolbar-less tables (e.g. the entity grid) keep the
          trigger in the leading column header below. */}
      {toolbarNode &&
        (floatingHeader && !isFullscreen ? (
          // Sticky-toolbar slot: the inner pin goes position:fixed on scroll
          // (see the controller effect); the slot reserves its height in flow.
          <div ref={toolbarSlotRef} className="editable-table-toolbar-slot">
            <div ref={toolbarFixedRef} className="editable-table-toolbar-pin">
              {toolbarNode}
            </div>
          </div>
        ) : (
          toolbarNode
        ))}

      {/* Table */}
      <div ref={scrollContainerRef} className={cn('editable-table-scroll', stickyMeta.hasStickyColumns && 'has-sticky-columns')}>
        <Table
          ref={tableRef}
          role="grid"
          tabIndex={0}
          aria-label={t('table.ariaLabel')}
          aria-rowcount={totalRows + 1}
          aria-colcount={totalCols}
          onKeyDown={handleKeyDown}
          className={cn('outline-none', layoutReady && 'editable-table--fixed')}
        >
          {/*
            Under fixed layout the <colgroup> is the single source of column
            widths — required because grouped headers render colSpan'd <th>s
            that can't carry per-column widths. During a drag the active
            <col> is mutated directly (see useColumnWidths) so the column
            tracks the pointer without any React re-renders.
          */}
          {layoutReady && (
            <colgroup>
              {hasLeadingCol && (
                <col style={{ width: ACTION_COL_WIDTH }} />
              )}
              {renderColumns.map((col) => (
                <col
                  key={col.data}
                  data-col-key={col.data}
                  style={{ width: `${col.width}px` }}
                />
              ))}
            </colgroup>
          )}
          <TableHeader>
            {renderHeaderRow(false)}
          </TableHeader>
          <TableBody>
            {/* Zero-row state — a single full-width row REPLACING the body, so
                the message can never sit alongside blank filler rows. The
                content is position:sticky (like the cap footer) so it stays
                centred in the viewport when the grid scrolls horizontally. */}
            {showEmptyState && (
              <TableRow className="editable-table-empty-row" role="presentation">
                <TableCell
                  colSpan={managedColumns.length + (hasLeadingCol ? 1 : 0)}
                  className="editable-table-empty-cell"
                >
                  <div className="editable-table-empty-content">
                    {renderEmptyState?.({ hasColumnFilters })}
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!showEmptyState && table.getRowModel().rows.map((row) => {
              const rowData = row.original;
              const rowIndex = row.index;
              const rowId = rowData._rowId as string;

              // Separator row (not memoized — simple and rare)
              if (isSeparatorRow(rowData)) {
                return (
                  <TableRow key={rowId} className="separator-row" aria-rowindex={rowIndex + 2}>
                    {hasLeadingCol && (
                      <TableCell
                        className={cn('separator-action-cell', stickyMeta.actionColSticky && 'cell-sticky')}
                        style={leadingCellStyle}
                      >
                        {!readOnly && !hideActionColumn && (
                          <ActionCell
                            isSeparator
                            monthKey={toDisplayString(rowData._monthKey)}
                            onAddRow={() => handleAddRow({ separatorRowId: rowId, monthKey: toDisplayString(rowData._monthKey) })}
                          />
                        )}
                      </TableCell>
                    )}
                    {stickyMeta.hasStickyColumns ? (
                      <>
                        <TableCell
                          colSpan={stickyMeta.offsets.size}
                          className="cell-sticky cell-sticky-last"
                          style={separatorLabelStyle}
                        >
                          <span className="separator-label">
                            {toDisplayString(rowData._separatorLabel)}
                          </span>
                        </TableCell>
                        {managedColumns.length - stickyMeta.offsets.size > 0 && (
                          <TableCell colSpan={managedColumns.length - stickyMeta.offsets.size} />
                        )}
                      </>
                    ) : (
                      <TableCell colSpan={managedColumns.length}>
                        <span className="separator-label">
                          {toDisplayString(rowData._separatorLabel)}
                        </span>
                      </TableCell>
                    )}
                  </TableRow>
                );
              }

              const rowState = rowId ? rowStates.get(rowId) : undefined;
              const rowChangedCells = rowId ? changedCells.get(rowId) : undefined;
              const rowCellErrors = rowId ? rowErrors.get(rowId) : undefined;
              const editingColId = editingCell?.rowId === rowId ? editingCell.colId : null;

              const rowReadOnly = isRowReadOnly ? isRowReadOnly(rowData as T) : false;

              // Expansion state
              const externalId = rowData[rowIdField] as string | undefined;
              const expandKey = externalId ?? rowId;
              const canRowExpand = hasExpandColumn && canExpand?.(rowData as T) === true;
              const isExpanded = canRowExpand && (expandedRows?.has(expandKey) ?? false);

              const elements = [
                <DataRow
                  key={rowId}
                  rowData={rowData}
                  rowIndex={rowIndex}
                  rowState={rowState}
                  rowChangedCells={rowChangedCells}
                  rowCellErrors={rowCellErrors}
                  columns={managedColumns}
                  readOnly={readOnly}
                  hideActionColumn={hideActionColumn}
                  actionColumnActions={actionColumnActions}
                  renderActionCell={renderActionCell as DataRowProps['renderActionCell']}
                  renderRowMenu={renderRowMenu as DataRowProps['renderRowMenu']}
                  rowIsReadOnly={rowReadOnly}
                  rowIdField={rowIdField}
                  editingColId={editingColId}
                  entityOptionsMap={entityOptionsMap}
                  entityOptionsLoading={entityOptionsLoading}
                  locale={locale}
                  cellClassName={cellClassName as DataRowProps['cellClassName']}
                  onCellClick={onCellClick as DataRowProps['onCellClick']}
                  onStartEditing={stableStartEditing}
                  onCellChange={stableCellChange}
                  onStartUndoGroup={startUndoGroup}
                  onEndUndoGroup={endUndoGroup}
                  onCommitEdit={commitEdit}
                  onCancelEdit={cancelEdit}
                  onRowRollback={handleRowRollback}
                  onRowDelete={handleRowDelete}
                  onSetFocusedCell={setFocusedCell}
                  getCellSelectionClasses={getCellSelectionClasses}
                  stickyMeta={stickyMeta}
                  cellStyleByCol={cellStyleByCol}
                  actionCellStyle={actionCellStyle}
                  showExpandColumn={hasExpandColumn}
                  isExpandable={canRowExpand}
                  isExpanded={isExpanded}
                  expandRowId={expandKey}
                  onRowExpand={stableOnRowExpand}
                  hideExpandMenuItem={hideExpandMenuItem}
                  expandTrigger={expandTrigger}
                />,
              ];

              // Expanded detail row (render-only — not in data model)
              if (isExpanded && expandedRowRender) {
                const showDetailActionCol = hasLeadingCol;
                elements.push(
                  <TableRow key={`${rowId}-expanded`} className="expanded-detail-row" role="presentation">
                    {showDetailActionCol && (
                      <TableCell
                        className={cn('rollback-cell', stickyMeta.actionColSticky && 'cell-sticky')}
                        style={leadingCellStyle}
                      />
                    )}
                    <TableCell colSpan={managedColumns.length} className="p-0 expanded-detail-cell">
                      {/* Entering the panel (click or tab) releases the parent
                          grid's cell focus — otherwise the previously selected
                          cell keeps capturing keystrokes typed in here. */}
                      <div
                        id={`${rowId}-detail`}
                        className="expanded-detail-content"
                        onMouseDownCapture={clearCellFocus}
                        onFocusCapture={clearCellFocus}
                      >
                        {expandedRowRender(rowData as T, { softDelete: () => handleRowDelete(rowId) })}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }

              return elements;
            })}
          </TableBody>
          {/* Bottom "Show all" affordance — a full-width <tfoot> cell (spans
              every column, so it spans the FULL scrollable table width). That
              width is what lets the bar's own content be position:sticky and
              stay pinned to the viewport's left edge while the grid scrolls
              horizontally; a plain element after the table would only be
              viewport-wide and scroll out of view. Only while the render cap
              is actively truncating. */}
          {renderCapFooter && isRenderCapped && (
            <tfoot>
              <tr>
                <td
                  colSpan={managedColumns.length + (hasLeadingCol ? 1 : 0)}
                  className="editable-table-cap-footer-cell"
                >
                  {renderCapFooter(visibleRowCount)}
                </td>
              </tr>
            </tfoot>
          )}
        </Table>

        {/* Fixed horizontal scrollbar pinned to viewport bottom */}
        <StickyScrollbar scrollRef={scrollContainerRef} isFullscreen={isFullscreen} />
      </div>

      {/* Window-snap floating header — a fixed, display-only mirror of the header
          row that pins to the top of the app content area while the grid scrolls
          past. Opt-in; off in fullscreen (the internal scroll container makes the
          native sticky header work there). Lives outside `.editable-table-scroll`
          so it can stick to the window instead of being trapped by that
          horizontal scroll container. */}
      {floatingHeader && !isFullscreen && layoutReady && (
        <FloatingTableHeader
          scrollContainerRef={scrollContainerRef}
          headerRowRef={headerRowRef}
          renderColumns={renderColumns}
          hasLeadingCol={hasLeadingCol}
          renderHeaderRow={renderHeaderRow}
          stickyOffset={toolbarHeight}
        />
      )}
    </div>
  );
}
